/**
 * The cut by one answer (R49): what can be cut on, and what every cut number has to say.
 *
 * Reading "qual item ficou mais difícil de encontrar" against everybody is the defect this whole
 * feature treats: the same answer is 30% or 75% depending on the base the reader imagines. The cut
 * fixes it by naming the group — "dentre quem marcou 'não encontrei um item'" — and the screen only
 * earns that if it says, on every number, which base it used.
 *
 * Three decisions live here because all three are judgements, not markup:
 *
 * - **which questions may be offered.** The BFF refuses `RATING`, `NPS`, `BOOLEAN` and free text,
 *   and a screen that offers what will be refused turns a refusal into the normal flow. The
 *   selector is built from {@link cuttableQuestions}, so a refusal is a net, never a step;
 * - **the pair is one value.** The two query params travel together — one without the other is a
 *   `400` — so the control emits `question + option` or nothing at all, and half a cut is
 *   unrepresentable rather than merely discouraged;
 * - **the sentences.** `filter.people` and `filter.totalSubmitted` come precisely so the screen can
 *   write "de 40 das 214 pessoas que enviaram", and the sentence about what is **not** cut is not a
 *   nicety: without it, a funnel standing beside cut charts reads as cut too.
 *
 * @module Pages/BUs/Surveys
 */

import { formatCount } from './surveyFormat';
import type {
  SurveyQuestionResult,
  SurveyResultsCut,
  SurveyResultsFilter,
} from '../Types/Surveys.types';

export interface CuttableOption {
  /** The stable code the BFF matches. Never the translated label. */
  value: string;
  label: string;
}

export interface CuttableQuestion {
  key: string;
  label: string;
  options: CuttableOption[];
}

/**
 * The questions of the version on screen that a cut may be made on.
 *
 * `SINGLE_CHOICE`, `MULTI_CHOICE` and the **rows** of a grid of those types — the ones answered by
 * pointing at an option with a stable `value`. A grid's mother is never answered (R25), so it is
 * the rows that are offered, each one carrying the grid's scale as its options and its own `key`,
 * which is what `filterQuestionKey` takes.
 *
 * A choice question with no options, and a grid with no scale, are dropped: they would be a group
 * heading with nothing under it.
 */
export const cuttableQuestions = (
  questions: SurveyQuestionResult[] | undefined,
): CuttableQuestion[] => {
  const cuttable: CuttableQuestion[] = [];

  (questions ?? []).forEach(question => {
    if (question.type === 'SINGLE_CHOICE' || question.type === 'MULTI_CHOICE') {
      const options = question.options ?? [];

      if (options.length > 0) {
        cuttable.push({
          key: question.key,
          label: question.label,
          options: options.map(option => ({ value: option.value, label: option.label })),
        });
      }

      return;
    }

    if (question.type !== 'MATRIX') return;

    const scale = question.scale ?? [];

    if (scale.length === 0) return;

    (question.rows ?? []).forEach(row => {
      if (row.type !== 'SINGLE_CHOICE' && row.type !== 'MULTI_CHOICE') return;

      cuttable.push({
        /* The ROW's key, never the grid's: the grid is what the BFF refuses, naming these rows. */
        key: row.key,
        label: `${question.label} — ${row.label}`,
        options: scale.map(level => ({ value: level.value, label: level.label })),
      });
    });
  });

  return cuttable;
};

/**
 * `\u001f`, the ASCII unit separator, because a `key` and a `value` are slugs and neither can hold
 * it. A separator that could appear inside either half would split the pair in the wrong place and
 * send a cut nobody asked for.
 */
const PAIR_SEPARATOR = '\u001f';

/** One `<option value>` carrying the whole pair — the only shape the BFF accepts. */
export const encodeCut = (questionKey: string, optionValue: string): string =>
  `${questionKey}${PAIR_SEPARATOR}${optionValue}`;

/** What the control currently holds, so the `<select>` stays controlled. `''` means no cut. */
export const cutValue = (cut: SurveyResultsCut | undefined): string =>
  cut ? encodeCut(cut.filterQuestionKey, cut.filterOptionValue) : '';

/**
 * Back to the pair, or `undefined` — never half of one.
 *
 * Anything that does not carry both halves is no cut at all: emitting one param without the other
 * is the `400` this encoding exists to make impossible.
 */
export const decodeCut = (value: string): SurveyResultsCut | undefined => {
  const [questionKey, optionValue] = value.split(PAIR_SEPARATOR);

  if (!questionKey || !optionValue) return undefined;

  return { filterQuestionKey: questionKey, filterOptionValue: optionValue };
};

/** Which group the screen is showing, in the labels the BFF already translated. */
export const cutHeadline = (filter: SurveyResultsFilter): string =>
  `Só quem respondeu “${filter.optionLabel}” em “${filter.questionLabel}”`;

/**
 * The base of every cut number, in words: "de 40 das 214 pessoas que enviaram".
 *
 * R22b taken one step further — the screen already says it counts people, and this says **which**
 * people. It is the whole reason `people` and `totalSubmitted` travel inside `filter`.
 */
export const cutBaseSentence = (filter: SurveyResultsFilter): string =>
  `de ${formatCount(filter.people)} das ${formatCount(filter.totalSubmitted)} pessoas que enviaram`;

/**
 * The sentence that keeps the rest of the screen honest, on the results screen.
 *
 * It names the blocks by what the reader sees, and then says why, because "só as perguntas" on its
 * own reads as an arbitrary limitation instead of the only defensible one.
 */
export const CUT_FULL_BASE_NOTICE =
  'Só os números por pergunta estão recortados. Os cartões do topo, o funil, a taxa de resposta, ' +
  'o público elegível e o tempo médio continuam na base cheia — quem abandonou no passo 1 não ' +
  'deixou resposta nenhuma, então nenhum recorte por resposta alcança essa gente, e um funil ' +
  'recortado descreveria um caminho que ninguém percorreu.';

/** The same sentence where the neighbours are the adoption cards, not a funnel. */
export const CUT_FULL_BASE_NOTICE_ADOPTION =
  'Só os números por pergunta estão recortados. Os cartões de adoção acima — quem ligou, quem ' +
  'está com a funcionalidade agora, quem voltou atrás — continuam na base cheia: eles contam ' +
  'gente que muitas vezes não respondeu ao questionário, e nenhum recorte por resposta alcança ' +
  'quem não respondeu.';

/** The mark each full-base block carries while a cut is on, so it is not read as cut. */
export const CUT_FULL_BASE_TAG = 'Base cheia — não entra no recorte';

/** Beside an export button, because a file that came out complete must not look cut. */
export const CUT_CSV_NOTICE =
  'O CSV continua saindo inteiro, sem recorte — ele é a matéria-prima, e o recorte se faz na planilha.';

/**
 * A cut that catches nobody, said out loud.
 *
 * `200` with everything at zero is a finding — "ninguém que deu esse motivo respondeu esta" — and
 * a screen that cannot say "ninguém" pushes the reader into guessing. Never an error state, and
 * the control stays where it is so the cut can be cleared.
 */
export const cutEmptyTitle = 'Ninguém marcou essa resposta';

export const cutEmptyDescription = (filter: SurveyResultsFilter): string =>
  `Nenhuma das ${formatCount(filter.totalSubmitted)} pessoas que enviaram respondeu ` +
  `“${filter.optionLabel}” em “${filter.questionLabel}”, então não há número por pergunta para ` +
  'mostrar. Isso é um resultado, não um erro: o recorte continua aplicado, e o controle acima o limpa.';

/** Whether the cut on screen caught nobody — the branch that replaces the charts with a sentence. */
export const isEmptyCut = (filter: SurveyResultsFilter | undefined): boolean =>
  filter !== undefined && filter.people === 0;
